import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ArrowLeft, ArrowRight, Ruler, Images } from 'lucide-react';
import { RetrofitSurveyData, getRetrofitSurveyAddress } from '../../utils/retrofitSurveyStorage';
import { calculateRooms } from '../../utils/floorPlanUtils';
import { getFloorsWithWalls, countFloorsWithWalls, getFloorsWithDataFromAllFloorData, countFloorsFromAllFloorData } from '../../utils/floorCountUtils';
import PhotoCapture from '../PhotoCapture';
import PhotoGalleryModal from '../PhotoGalleryModal';
import type { PhotoData } from '../../services/CameraService';
import { useRetrofitPhotoUpload } from '../../hooks/useRetrofitPhotoUpload';
import { analyzeSectionFileSize, logFileSizeAnalysis } from '../../utils/fileSizeAnalysis';
import { deletePhotoFromSupabase } from '../../utils/supabaseStorage';

// Interfaces
export interface MainPropertyMeasurementsFormProps {
  onNavigateNext: () => void;
  onNavigateBack: () => void;
  floorPlanId?: string;
  surveyData?: RetrofitSurveyData;
  surveyId?: string;
  onDataUpdate?: (updater: (prev: RetrofitSurveyData) => RetrofitSurveyData, sectionId?: string) => void;
  onSaveImmediately?: () => Promise<string>;
}

interface StoreyMeasurement {
  id: string; // 'storey-0', 'storey-1'
  name: string; // 'GF', 'FF', 'SF' etc.
  height: string;
}

interface DisplayablePhotoData extends PhotoData {
  displayId: string;
  dataUrl: string;
  fileName?: string;
  url?: string;
  uploading?: boolean;
  error?: string;
  note?: string;
}

interface MeasurementsFormData {
  numberOfStoreys: string;
  hasCellar: boolean | null;
  cellarLength: string;
  cellarWidth: string;
  cellarArea: string; // Auto-calculated
  totalArea: string; // Read-only for now
}

const numberOptions = (max: number) => {
    const options = [{ value: "", label: "Select number of storeys" }];
    for (let i = 1; i <= max; i++) {
        options.push({ value: i.toString(), label: i.toString() });
    }
    return options;
};

// Component
// Helper function to calculate number of storeys from floor plan data
const calculateNumberOfStoreysFromFloorPlan = (surveyData?: RetrofitSurveyData): string => {
  if (!surveyData?.floor_plan?.linkedFloorPlanData) {
    console.log('📐 [MainPropertyMeasurements] No linkedFloorPlanData found for storeys calculation');
    return '';
  }

  const floorPlanData = surveyData.floor_plan.linkedFloorPlanData;
  
  // Method 1: Try allFloorData - only count floors with actual data
  const allFloorData = floorPlanData.allFloorData || {};
  const floorsWithData = getFloorsWithDataFromAllFloorData(allFloorData);
  
  if (floorsWithData.length > 0) {
    console.log('📐 [MainPropertyMeasurements] Calculated storeys from allFloorData (only floors with data):', {
      count: floorsWithData.length,
      floors: floorsWithData
    });
    return floorsWithData.length.toString();
  }
  
  // Method 2: Try floorPlans object - only count floors with actual walls
  if (floorPlanData.floorPlans) {
    const floorsWithWalls = getFloorsWithWalls(floorPlanData.floorPlans);
    if (floorsWithWalls.length > 0) {
      console.log('📐 [MainPropertyMeasurements] Calculated storeys from floorPlans (only floors with walls):', {
        count: floorsWithWalls.length,
        floors: floorsWithWalls
      });
      return floorsWithWalls.length.toString();
    }
  }
  
  console.log('📐 [MainPropertyMeasurements] Could not calculate storeys from floor plan data');
  return '';
};

// Helper function to calculate total area from floor plan data
const calculateTotalAreaFromFloorPlan = (surveyData?: RetrofitSurveyData): string => {
  if (!surveyData?.floor_plan?.linkedFloorPlanData) {
    console.log('📐 [MainPropertyMeasurements] No linkedFloorPlanData found');
    return '0.00';
  }

  const floorPlanData = surveyData.floor_plan.linkedFloorPlanData;
  
  // Debug: log the structure of linkedFloorPlanData
  console.log('📐 [MainPropertyMeasurements] linkedFloorPlanData structure:', {
    hasAllFloorData: !!floorPlanData.allFloorData,
    hasFloorPlans: !!floorPlanData.floorPlans,
    hasRooms: !!floorPlanData.rooms,
    keys: Object.keys(floorPlanData)
  });
  
  let totalArea = 0;
  let calculationMethod = 'none';
  
  // Method 1: Try allFloorData (preferred method for multi-floor)
  const allFloorData = floorPlanData.allFloorData || {};
  const availableFloors = Object.keys(allFloorData).map(Number);
  
  if (availableFloors.length > 0) {
    availableFloors.forEach(floorNumber => {
      const floorData = allFloorData[floorNumber];
      if (floorData?.floorMetrics?.totalArea) {
        totalArea += floorData.floorMetrics.totalArea;
        calculationMethod = 'allFloorData.floorMetrics';
      } else if (floorData?.rooms) {
        const roomsArea = floorData.rooms.reduce((sum: number, room: any) => sum + (room.area || 0), 0);
        totalArea += roomsArea;
        calculationMethod = 'allFloorData.rooms';
      }
    });
  }
  
  // Method 2: Try direct rooms array (fallback for single floor or legacy data)
  if (totalArea === 0 && floorPlanData.rooms && Array.isArray(floorPlanData.rooms)) {
    totalArea = floorPlanData.rooms.reduce((sum: number, room: any) => sum + (room.area || 0), 0);
    calculationMethod = 'direct rooms';
    console.log('📐 [MainPropertyMeasurements] Using direct rooms:', {
      roomCount: floorPlanData.rooms.length,
      totalArea: totalArea.toFixed(2)
    });
  }
  
  // Method 3: Try floorPlans object (contains walls - calculate rooms from walls)
  if (totalArea === 0 && floorPlanData.floorPlans) {
    const floorNumbers = Object.keys(floorPlanData.floorPlans).map(Number);
    console.log('📐 [MainPropertyMeasurements] Calculating area from walls in floorPlans');
    console.log('📐 [MainPropertyMeasurements] Available floors:', floorNumbers);
    
    const roomCustomLabels = floorPlanData.roomCustomLabels || {};
    
    floorNumbers.forEach(floorNum => {
      const walls = floorPlanData.floorPlans[floorNum];
      if (Array.isArray(walls) && walls.length > 0) {
        try {
          // Calculate rooms from walls for this floor
          const rooms = calculateRooms(walls, roomCustomLabels, 'geometryUpdate', floorNum);
          const floorArea = rooms.reduce((sum: number, room: any) => sum + (room.area || 0), 0);
          totalArea += floorArea;
          calculationMethod = 'floorPlans walls → rooms';
          
          console.log(`📐 [MainPropertyMeasurements] Floor ${floorNum}: ${rooms.length} rooms, ${floorArea.toFixed(2)}m²`);
        } catch (error) {
          console.error(`❌ [MainPropertyMeasurements] Error calculating rooms for floor ${floorNum}:`, error);
        }
      }
    });
  }
  
  // Method 4: Check all keys to find rooms/area data
  if (totalArea === 0) {
    const keys = Object.keys(floorPlanData);
    console.log('📐 [MainPropertyMeasurements] All keys in linkedFloorPlanData:', keys);
    console.log('📐 [MainPropertyMeasurements] Sample values:', {
      currentFloor: floorPlanData.currentFloor,
      currentWalls: Array.isArray(floorPlanData.currentWalls) ? `Array(${floorPlanData.currentWalls.length})` : typeof floorPlanData.currentWalls,
      elements: Array.isArray(floorPlanData.elements) ? `Array(${floorPlanData.elements.length})` : typeof floorPlanData.elements,
      totalArea: floorPlanData.totalArea,
      hlpLength: floorPlanData.hlpLength,
      pwLength: floorPlanData.pwLength
    });
    
    // Try direct totalArea field
    if (floorPlanData.totalArea && typeof floorPlanData.totalArea === 'number') {
      totalArea = floorPlanData.totalArea;
      calculationMethod = 'direct totalArea field';
      console.log('📐 [MainPropertyMeasurements] Using direct totalArea field:', totalArea);
    }
  }
  
  console.log('📐 [MainPropertyMeasurements] Calculated total area from floor plan:', {
    availableFloors,
    totalArea: totalArea.toFixed(2),
    calculationMethod,
    floorBreakdown: availableFloors.map(f => ({
      floor: f,
      area: allFloorData[f]?.floorMetrics?.totalArea?.toFixed(2) || '0.00'
    }))
  });
  
  return totalArea.toFixed(2);
};

const MainPropertyMeasurementsForm: React.FC<MainPropertyMeasurementsFormProps> = ({
  onNavigateNext,
  onNavigateBack,
  floorPlanId,
  surveyData,
  surveyId,
  onDataUpdate
}) => {
  // Initialize photo upload queue with explicit surveyId and property name for better file organization
  const propertyName = surveyData ? getRetrofitSurveyAddress(surveyData) : undefined;
  const { queuePhotoUpload } = useRetrofitPhotoUpload(
    surveyId || surveyData?.id || floorPlanId || undefined,
    propertyName
  );

  // Calculate total area and number of storeys from floor plan live calculations
  const calculatedTotalArea = calculateTotalAreaFromFloorPlan(surveyData);
  const calculatedNumberOfStoreys = calculateNumberOfStoreysFromFloorPlan(surveyData);

  const [formData, setFormData] = useState<MeasurementsFormData>({
    numberOfStoreys: calculatedNumberOfStoreys || surveyData?.main_property_measurements?.numberOfStoreys || '',
    hasCellar: surveyData?.main_property_measurements?.hasCellar ?? null,
    cellarLength: (surveyData?.main_property_measurements as any)?.cellarLength || '',
    cellarWidth: (surveyData?.main_property_measurements as any)?.cellarWidth || '',
    cellarArea: (surveyData?.main_property_measurements as any)?.cellarArea || '0.00',
    totalArea: calculatedTotalArea,
  });

  // Cellar photos state
  const [cellarPhotos, setCellarPhotos] = useState<DisplayablePhotoData[]>(
    (surveyData?.main_property_measurements as any)?.cellarPhotos || []
  );
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);

  const [storeyMeasurements, setStoreyMeasurements] = useState<StoreyMeasurement[]>(
    surveyData?.main_property_measurements?.storeyMeasurements || []
  );

  // Debounce timer for dropdown autosave (defined early for use in useEffect)
  const autosaveTimerRef = useRef<NodeJS.Timeout | null>(null);
  // Track recent changes to prevent sync during immediate autosaves
  const recentChangeRef = useRef(false);
  // Track last synced data to prevent loops
  const lastSyncedDataRef = useRef<string>('');
  
  // Load saved storey measurements on mount
  useEffect(() => {
    if (surveyData?.main_property_measurements?.storeyMeasurements) {
      console.log('🏢 [MainPropertyMeasurements] Loading saved storey measurements:', surveyData.main_property_measurements.storeyMeasurements);
      setStoreyMeasurements(surveyData.main_property_measurements.storeyMeasurements);
    }
  }, [surveyData?.main_property_measurements?.storeyMeasurements]);
  
  // Update total area and number of storeys when floor plan data changes
  useEffect(() => {
    const newTotalArea = calculateTotalAreaFromFloorPlan(surveyData);
    const newNumberOfStoreys = calculateNumberOfStoreysFromFloorPlan(surveyData);
    
    if (newTotalArea !== formData.totalArea || newNumberOfStoreys !== formData.numberOfStoreys) {
      setFormData(prev => ({
        ...prev,
        totalArea: newTotalArea,
        numberOfStoreys: newNumberOfStoreys || prev.numberOfStoreys
      }));
      
      // Auto-update parent data
      if (onDataUpdate) {
        onDataUpdate(prev => ({
          ...prev,
          main_property_measurements: {
            ...prev.main_property_measurements,
            totalArea: newTotalArea,
            numberOfStoreys: newNumberOfStoreys || prev.main_property_measurements?.numberOfStoreys
          }
        }), 'main_property_measurements');
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surveyData?.floor_plan?.linkedFloorPlanData, onDataUpdate]);
  
  // Sync surveyData prop changes back to form state (but not while autosave is pending or recently changed)
  useEffect(() => {
    // Don't sync if we have a pending autosave or just made a change
    if (surveyData?.main_property_measurements && !autosaveTimerRef.current && !recentChangeRef.current) {
      const incoming = JSON.stringify(surveyData.main_property_measurements);
      
      if (incoming !== lastSyncedDataRef.current) {
        lastSyncedDataRef.current = incoming;
        const recalculatedTotalArea = calculateTotalAreaFromFloorPlan(surveyData);
        const recalculatedNumberOfStoreys = calculateNumberOfStoreysFromFloorPlan(surveyData);
        setFormData({
          numberOfStoreys: recalculatedNumberOfStoreys || surveyData.main_property_measurements.numberOfStoreys || '',
          hasCellar: surveyData.main_property_measurements.hasCellar ?? null,
          cellarLength: (surveyData.main_property_measurements as any).cellarLength || '',
          cellarWidth: (surveyData.main_property_measurements as any).cellarWidth || '',
          cellarArea: (surveyData.main_property_measurements as any).cellarArea || '0.00',
          totalArea: recalculatedTotalArea,
        });
      }
    }
  }, [surveyData]);

  const getStoreyName = (index: number): string => {
    if (index === 0) return 'GF';
    if (index === 1) return 'FF';
    if (index === 2) return 'SF';
    return `${index + 1}F`;
  };

  // Auto-calculate cellar area when length or width changes
  useEffect(() => {
    const length = parseFloat(formData.cellarLength) || 0;
    const width = parseFloat(formData.cellarWidth) || 0;
    const calculatedArea = (length * width).toFixed(2);
    
    if (calculatedArea !== formData.cellarArea) {
      setFormData(prev => ({
        ...prev,
        cellarArea: calculatedArea
      }));
      
      // Auto-update parent data
      if (onDataUpdate) {
        onDataUpdate(prev => ({
          ...prev,
          main_property_measurements: {
            ...prev.main_property_measurements,
            cellarArea: calculatedArea
          }
        }), 'main_property_measurements');
      }
    }
  }, [formData.cellarLength, formData.cellarWidth, formData.cellarArea, onDataUpdate]);

  useEffect(() => {
    const numStoreys = parseInt(formData.numberOfStoreys, 10) || 0;
    
    // Try to get saved measurements from surveyData first, then fall back to current state
    const savedMeasurements = surveyData?.main_property_measurements?.storeyMeasurements || [];
    const currentMeasurements = storeyMeasurements;
    
    const newMeasurements: StoreyMeasurement[] = Array.from({ length: numStoreys }, (_, i) => {
        // Priority: saved data > current state > default empty
        return savedMeasurements[i] || currentMeasurements[i] || {
            id: `storey-${i}`,
            name: getStoreyName(i),
            height: '',
        };
    });
    
    const updatedMeasurements = newMeasurements.slice(0, numStoreys);
    setStoreyMeasurements(updatedMeasurements);
    
    console.log('🏢 [MainPropertyMeasurements] Storey measurements created/updated:', {
      numStoreys,
      savedCount: savedMeasurements.length,
      currentCount: currentMeasurements.length,
      finalCount: updatedMeasurements.length,
      measurements: updatedMeasurements
    });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.numberOfStoreys]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    const isSelect = e.target instanceof HTMLSelectElement;
    
    // Mark recent change to prevent sync
    recentChangeRef.current = true;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Trigger autosave via parent callback
    if (onDataUpdate) {
      // Clear any existing timer
      if (autosaveTimerRef.current) {
        clearTimeout(autosaveTimerRef.current);
      }

      const triggerAutosave = () => {
        onDataUpdate(prev => ({
          ...prev,
          main_property_measurements: {
            ...prev.main_property_measurements,
            [name]: value
          }
        }), 'main_property_measurements');
        // Allow sync after autosave completes
        setTimeout(() => {
          recentChangeRef.current = false;
        }, 100);
      };

      // Debounce autosave for dropdowns (300ms after user stops changing)
      if (isSelect) {
        autosaveTimerRef.current = setTimeout(triggerAutosave, 300);
      } else {
        triggerAutosave();
      }
    }
  };

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (autosaveTimerRef.current) {
        clearTimeout(autosaveTimerRef.current);
      }
    };
  }, []);
  
  const handleBooleanChange = (name: keyof MeasurementsFormData, value: boolean) => {
    // Mark recent change to prevent sync
    recentChangeRef.current = true;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Trigger autosave via parent callback
    if (onDataUpdate) {
      onDataUpdate(prev => ({
        ...prev,
        main_property_measurements: {
          ...prev.main_property_measurements,
          [name]: value
        }
      }), 'main_property_measurements');
      // Allow sync after autosave completes
      setTimeout(() => {
        recentChangeRef.current = false;
      }, 100);
    }
  };
  
  const handleStoreyInputChange = (index: number, field: 'height', value: string) => {
      const updatedMeasurements = [...storeyMeasurements];
      if(updatedMeasurements[index]) {
          updatedMeasurements[index] = { ...updatedMeasurements[index], [field]: value };
          setStoreyMeasurements(updatedMeasurements);
          
          console.log('🏢 [MainPropertyMeasurements] Height changed:', {
            storeyId: updatedMeasurements[index].id,
            oldValue: storeyMeasurements[index]?.[field],
            newValue: value,
            updatedMeasurements
          });
          
          // Trigger autosave via parent callback
          if (onDataUpdate) {
            onDataUpdate(prev => ({
              ...prev,
              main_property_measurements: {
                ...prev.main_property_measurements,
                storeyMeasurements: updatedMeasurements
              }
            }));
            console.log('🏢 [MainPropertyMeasurements] ✅ onDataUpdate called with height:', value);
          }
      }
  };

  // 📊 FILE SIZE ANALYSIS: Photo fields for main_property_measurements section
  const MAIN_PROPERTY_PHOTO_FIELDS = [
    'cellarPhotos'
  ];

  // 📊 FILE SIZE ANALYSIS: Analyze main_property_measurements section
  const analyzeMainPropertyMeasurementsFileSize = useCallback(() => {
    const result = analyzeSectionFileSize(
      'main_property_measurements',
      surveyData?.main_property_measurements,
      MAIN_PROPERTY_PHOTO_FIELDS
    );
    logFileSizeAnalysis('main_property_measurements', result, 'cleanupMainPropertyMeasurementsBase64');
  }, [surveyData?.main_property_measurements]);

  // Run analysis on data load
  useEffect(() => {
    if (surveyData?.main_property_measurements) {
      const timer = setTimeout(() => analyzeMainPropertyMeasurementsFileSize(), 1000);
      return () => clearTimeout(timer);
    }
  }, [surveyData?.main_property_measurements, analyzeMainPropertyMeasurementsFileSize]);

  // Expose to window for manual testing
  useEffect(() => {
    (window as any).analyzeMainPropertyMeasurements = analyzeMainPropertyMeasurementsFileSize;
    return () => {
      delete (window as any).analyzeMainPropertyMeasurements;
    };
  }, [analyzeMainPropertyMeasurementsFileSize]);

  // 🧹 CLEANUP: Strip base64 from photos that already have CDN URLs
  const cleanupBase64FromCDNPhotos = useCallback(() => {
    console.log('🧹 [BASE64 CLEANUP - MAIN_PROPERTY] Checking for photos with CDN URLs that still have base64...');
    
    let cleanupNeeded = false;
    
    // Clean cellar photos
    const cleanedPhotos = cellarPhotos.map((photo: any) => {
      if (photo && photo.url && photo.url.startsWith('http') && (photo.dataUrl || photo.base64String)) {
        console.log(`🧹 [BASE64 CLEANUP - MAIN_PROPERTY] Stripping base64 from cellar photo ${photo.displayId}`);
        cleanupNeeded = true;
        return {
          displayId: photo.displayId,
          format: photo.format,
          fileName: photo.fileName,
          url: photo.url,
          uploading: false,
          error: undefined
        };
      }
      return photo;
    });
    
    if (cleanupNeeded) {
      console.log('🧹 [BASE64 CLEANUP - MAIN_PROPERTY] ✅ Cleaned photos, updating state and saving...');
      setCellarPhotos(cleanedPhotos);
      
      // Save cleaned photos to database
      if (onDataUpdate) {
        setTimeout(() => {
          console.log('🧹 [BASE64 CLEANUP - MAIN_PROPERTY] 💾 Saving cleaned photos to database');
          onDataUpdate(prevData => ({
            ...prevData,
            main_property_measurements: {
              ...prevData.main_property_measurements,
              ...formData,
              storeyMeasurements,
              cellarPhotos: cleanedPhotos
            }
          }), 'main_property_measurements');
        }, 100);
      }
    } else {
      console.log('🧹 [BASE64 CLEANUP - MAIN_PROPERTY] No cleanup needed, all photos are clean');
    }
  }, [cellarPhotos, formData, storeyMeasurements, onDataUpdate]);

  // Expose cleanup function to window
  useEffect(() => {
    (window as any).cleanupMainPropertyMeasurementsBase64 = cleanupBase64FromCDNPhotos;
    return () => {
      delete (window as any).cleanupMainPropertyMeasurementsBase64;
    };
  }, [cleanupBase64FromCDNPhotos]);

  // 🧹 AUTO-CLEANUP: Automatically clean base64 from photos with CDN URLs on page load
  useEffect(() => {
    if (!cellarPhotos.length) return;
    
    // Check if any photos have CDN URLs but still have base64
    const needsCleanup = cellarPhotos.some((p: any) => 
      p && p.url && p.url.startsWith('http') && (p.dataUrl || p.base64String)
    );
    
    if (needsCleanup) {
      console.log('🧹 [AUTO-CLEANUP - MAIN_PROPERTY] Detected photos with CDN + base64, running automatic cleanup in 3 seconds...');
      const timer = setTimeout(() => {
        cleanupBase64FromCDNPhotos();
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [cellarPhotos, cleanupBase64FromCDNPhotos]);

  // Handle cellar photo capture
  const handleCellarPhotosCaptured = (capturedPhotos: PhotoData[]) => {
    console.log(`📸 [MainPropertyMeasurements] Cellar photos captured: ${capturedPhotos.length}`);

    setCellarPhotos(prev => {
      const newPhotoData: DisplayablePhotoData[] = capturedPhotos.map((photo, index) => {
        const displayId = `${Date.now()}_${index}_${Date.now()}`;
        const dataUrl = `data:image/${photo.format};base64,${photo.base64String}`;
        return {
          base64String: photo.base64String,
          format: photo.format,
          displayId,
          dataUrl,
          uploading: true
        };
      });

      const updatedPhotos = [...prev, ...newPhotoData];

      // Queue background uploads for new photos
      newPhotoData.forEach((photo: DisplayablePhotoData) => {
        queuePhotoUpload(
          photo.displayId,
          photo.dataUrl,
          'main_property_measurements',
          'cellar',
          (url: string) => {
            console.log(`✅ [MainPropertyMeasurements] Cellar photo uploaded to CDN:`, url);
            // ✅ CRITICAL: Use explicit field selection, NO spread operator
            setCellarPhotos(current =>
              current.map(p =>
                p.displayId === photo.displayId
                  ? { 
                      displayId: p.displayId,
                      format: p.format,
                      fileName: p.fileName,
                      url, // ✅ CDN URL
                      uploading: false,
                      error: undefined,
                      dataUrl: '', // Empty after upload
                      base64String: '' // Empty after upload
                    } as DisplayablePhotoData
                  : p
              )
            );

            // Update survey data with CDN URL
            if (onDataUpdate) {
              setTimeout(() => {
                setCellarPhotos(currentPhotos => {
                  console.log(`💾 [MainPropertyMeasurements] Saving cellar photo with CDN URL`);
                  onDataUpdate(prev => ({
                    ...prev,
                    main_property_measurements: {
                      ...prev.main_property_measurements,
                      cellarPhotos: currentPhotos
                    }
                  }), 'main_property_measurements');
                  return currentPhotos;
                });
              }, 0);
            }
          },
          (error: string) => {
            console.error(`❌ [MainPropertyMeasurements] Cellar photo upload failed:`, error);
            setCellarPhotos(current =>
              current.map(p =>
                p.displayId === photo.displayId
                  ? { ...p, uploading: false, error }
                  : p
              )
            );
          }
        );
      });

      // Update survey data
      if (onDataUpdate) {
        onDataUpdate(prev => ({
          ...prev,
          main_property_measurements: {
            ...prev.main_property_measurements,
            cellarPhotos: updatedPhotos
          }
        }), 'main_property_measurements');
      }

      return updatedPhotos;
    });
  };

  // Handle delete cellar photo
  const handleDeleteCellarPhoto = async (displayIdToRemove: string) => {
    console.log(`[MainMeasurements] 🗑️ Removing cellar photo, displayId: ${displayIdToRemove}`);
    
    // Find and delete from Supabase Storage
    const photoToDelete = cellarPhotos.find(p => p.displayId === displayIdToRemove);
    if (photoToDelete) {
      let filePath = (photoToDelete as any).fileName;
      if (!filePath && photoToDelete.url?.includes('/storage/v1/object/public/')) {
        filePath = photoToDelete.url.split('/storage/v1/object/public/')[1]?.split('/').slice(1).join('/');
      }
      if (filePath) {
        try {
          await deletePhotoFromSupabase(filePath, 'retrofit-survey-images');
          console.log(`[MainMeasurements] ✅ Deleted from storage: ${filePath}`);
        } catch (error) {
          console.warn(`[MainMeasurements] ⚠️ Failed to delete from storage:`, error);
        }
      }
    }
    
    setCellarPhotos(prev => {
      const filtered = prev.filter(p => p.displayId !== displayIdToRemove);
      
      // Update survey data
      if (onDataUpdate) {
        onDataUpdate(prevData => ({
          ...prevData,
          main_property_measurements: {
            ...prevData.main_property_measurements,
            cellarPhotos: filtered
          }
        }), 'main_property_measurements');
      }
      
      return filtered;
    });
  };

  // Handle update cellar photo note
  const handleUpdateCellarPhotoNote = (displayId: string, note: string) => {
    setCellarPhotos(prev => {
      const updated = prev.map(p =>
        p.displayId === displayId ? { ...p, note } : p
      );
      
      // Update survey data
      if (onDataUpdate) {
        onDataUpdate(prevData => ({
          ...prevData,
          main_property_measurements: {
            ...prevData.main_property_measurements,
            cellarPhotos: updated
          }
        }), 'main_property_measurements');
      }
      
      return updated;
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Main Property Measurements Data:', { floorPlanId, formData, storeyMeasurements, cellarPhotos });
    onNavigateNext();
  };

  const commonInputClassName = "w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none";
  const fieldWrapperClass = "space-y-2";
  const labelClass = "text-sm font-medium text-gray-700 flex items-center";
  const requiredMarker = <span className="text-red-500 mr-1">*</span>;
  const booleanButtonClass = (isSelected: boolean | null, actualValue: boolean) =>
    `flex-1 py-3 rounded-lg border-2 ${isSelected === actualValue ? 'bg-yellow-100 border-yellow-400 text-yellow-700' : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'} transition-colors font-medium`;


  return (
    <div className="bg-white/80 backdrop-blur-sm p-6 sm:p-8 rounded-xl shadow-lg border border-yellow-200 min-h-[calc(100vh-12rem)] flex flex-col">
      <h2 className="text-xl sm:text-2xl font-bold text-gray-900 border-b-2 border-gray-200 pb-4 mb-6 flex items-center gap-3">
        <Ruler />
        Main Property Measurements
      </h2>
      <form onSubmit={handleSubmit} className="space-y-6 flex-1 flex flex-col">
        <div className="flex-1 space-y-6">
          <div className={fieldWrapperClass}>
            <label htmlFor="numberOfStoreys" className={labelClass}>{requiredMarker}Number of storeys in Main Building</label>
            <div className="relative">
              <input 
                type="text" 
                id="numberOfStoreys" 
                name="numberOfStoreys" 
                value={formData.numberOfStoreys} 
                className={`${commonInputClassName} bg-blue-50 border-blue-300 text-blue-900 font-semibold cursor-not-allowed`} 
                readOnly 
                required
                title="Automatically calculated from floor plan data"
              />
              <div className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-blue-600 font-medium pointer-events-none">
                Auto-calculated
              </div>
            </div>
            <p className="text-xs text-blue-600 mt-1">✓ Automatically calculated from floor plan data</p>
          </div>

          {storeyMeasurements.map((storey, index) => (
            <fieldset key={storey.id} className="border border-gray-300 p-4 rounded-lg">
                <legend className="text-md font-semibold text-gray-700 px-2">{storey.name} - Main Property Measurements</legend>
                <div className="mt-2">
                    <div className={fieldWrapperClass}>
                        <label htmlFor={`${storey.id}-height`} className={labelClass}>{requiredMarker}Height (m)</label>
                        <input type="number" id={`${storey.id}-height`} value={storey.height} onChange={(e) => handleStoreyInputChange(index, 'height', e.target.value)} placeholder="e.g. 2.4" className={commonInputClassName} required step="0.01" min="0" />
                    </div>
                </div>
            </fieldset>
          ))}
          
          <div className={fieldWrapperClass}>
            <label className={labelClass}>{requiredMarker}Is there a cellar in the property?</label>
            <div className="flex space-x-4 mt-2">
                <button type="button" onClick={() => handleBooleanChange('hasCellar', true)} className={booleanButtonClass(formData.hasCellar, true)}>Yes</button>
                <button type="button" onClick={() => handleBooleanChange('hasCellar', false)} className={booleanButtonClass(formData.hasCellar, false)}>No</button>
            </div>
          </div>

          {/* Cellar Details - Show when hasCellar is true */}
          {formData.hasCellar === true && (
            <fieldset className="border-2 border-amber-300 bg-amber-50/50 p-5 rounded-xl space-y-4">
              <legend className="text-md font-semibold text-amber-800 px-3 bg-amber-100 rounded-lg border border-amber-300">Cellar Details</legend>
              
              {/* Cellar Dimensions */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className={fieldWrapperClass}>
                  <label htmlFor="cellarLength" className={labelClass}>{requiredMarker}Length (m)</label>
                  <input 
                    type="number" 
                    id="cellarLength" 
                    name="cellarLength" 
                    value={formData.cellarLength} 
                    onChange={handleInputChange}
                    placeholder="e.g. 5.0" 
                    className={commonInputClassName} 
                    required={formData.hasCellar === true}
                    step="0.01" 
                    min="0" 
                  />
                </div>

                <div className={fieldWrapperClass}>
                  <label htmlFor="cellarWidth" className={labelClass}>{requiredMarker}Width (m)</label>
                  <input 
                    type="number" 
                    id="cellarWidth" 
                    name="cellarWidth" 
                    value={formData.cellarWidth} 
                    onChange={handleInputChange}
                    placeholder="e.g. 4.0" 
                    className={commonInputClassName} 
                    required={formData.hasCellar === true}
                    step="0.01" 
                    min="0" 
                  />
                </div>
              </div>

              {/* Auto-calculated Cellar Area */}
              <div className={fieldWrapperClass}>
                <label htmlFor="cellarArea" className={labelClass}>Cellar Area (m²)</label>
                <div className="relative">
                  <input 
                    type="text" 
                    id="cellarArea" 
                    name="cellarArea" 
                    value={formData.cellarArea} 
                    className={`${commonInputClassName} bg-green-50 border-green-300 text-green-900 font-semibold cursor-not-allowed`} 
                    readOnly 
                    title="Automatically calculated from length × width"
                  />
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-green-600 font-medium pointer-events-none">
                    Auto-calculated
                  </div>
                </div>
                <p className="text-xs text-green-600 mt-1">✓ Automatically calculated from length × width</p>
              </div>

              {/* Cellar Images */}
              <div className={fieldWrapperClass}>
                <label className={`${labelClass} mb-3`}>
                  <Images className="w-4 h-4 mr-2" />
                  Cellar Images
                </label>
                <div className="space-y-3">
                  <PhotoCapture
                    onPhotosCaptured={handleCellarPhotosCaptured}
                    buttonLabel="Take Photos"
                    maxPhotos={10}
                  />

                  {cellarPhotos.length > 0 && (
                    <div>
                      <button
                        type="button"
                        onClick={() => setIsGalleryOpen(true)}
                        className="relative w-full aspect-video rounded-xl overflow-hidden border-2 border-amber-300 hover:border-amber-400 transition-all group shadow-md"
                      >
                        <img
                          src={cellarPhotos[0].url || cellarPhotos[0].dataUrl}
                          alt="Cellar"
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-black/40 group-hover:bg-black/60 transition-all flex items-center justify-center">
                          <div className="text-white text-center">
                            <Images className="w-8 h-8 mx-auto mb-2" />
                            <div className="text-xl font-bold">{cellarPhotos.length}</div>
                            <div className="text-sm">Click to view all photos</div>
                          </div>
                        </div>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </fieldset>
          )}

          <div className={fieldWrapperClass}>
            <label htmlFor="totalArea" className={labelClass}>Total Area of main building (m²)</label>
            <div className="relative">
              <input 
                type="text" 
                id="totalArea" 
                name="totalArea" 
                value={formData.totalArea} 
                className={`${commonInputClassName} bg-blue-50 border-blue-300 text-blue-900 font-semibold cursor-not-allowed`} 
                readOnly 
                title="Calculated from floor plan live calculations (all floors)"
              />
              <div className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-blue-600 font-medium pointer-events-none">
                Auto-calculated
              </div>
            </div>
            <p className="text-xs text-blue-600 mt-1">✓ Automatically calculated from floor plan live calculations (all floors)</p>
          </div>
        </div>

        <div className="mt-auto pt-6 border-t border-gray-200 flex flex-col sm:flex-row gap-3 sm:justify-between">
          <button type="button" onClick={onNavigateBack} className="w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-3 bg-gray-100 text-gray-700 font-semibold rounded-lg shadow-sm hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-offset-2 transition-all duration-200 transform hover:scale-[1.02]">
            <ArrowLeft size={18} /> Previous
          </button>
          <button type="submit" className="w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-semibold rounded-lg shadow-md hover:from-blue-600 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 transition-all duration-200 transform hover:scale-[1.02]">
            Next <ArrowRight size={18} />
          </button>
        </div>
      </form>

      {/* Photo Gallery Modal */}
      {isGalleryOpen && (
        <PhotoGalleryModal
          isOpen={isGalleryOpen}
          onClose={() => setIsGalleryOpen(false)}
          photos={cellarPhotos.map(photo => ({
            id: photo.displayId,
            url: photo.url || photo.dataUrl, // ✅ Use CDN URL if available, fallback to dataUrl
            localPath: photo.url || photo.dataUrl,
            fileName: photo.fileName
          }))}
          sectionTitle="Cellar Images"
          onDeletePhoto={handleDeleteCellarPhoto}
          onUpdateNote={handleUpdateCellarPhotoNote}
        />
      )}
    </div>
  );
};

export default MainPropertyMeasurementsForm; 